"use client";
import { lazy, Suspense, useEffect, useState } from "react";
import { AppProviders } from "./app/providers/AppProviders";
import { Shell } from "./features/desk/components/Shell";
import { ApiStatusProvider } from "./features/desk/lib/status";
import { AskView } from "./features/desk/views/AskView";
import { CommandPalette } from "./features/desk/views/CommandPalette";
import { CopyCandidatesView } from "./features/desk/views/CopyCandidatesView";
import { FlaggedFlowView } from "./features/desk/views/FlaggedFlowView";
import { FlaggedView } from "./features/desk/views/FlaggedView";
import { TopTradersView } from "./features/desk/views/TopTradersView";
import { UnusualMarketsView } from "./features/desk/views/UnusualMarketsView";
import { WalletDrawer } from "./features/desk/views/WalletDrawer";
import { WatchlistView } from "./features/desk/views/WatchlistView";
import { WhalesOverviewView } from "./features/desk/views/WhalesOverviewView";
import { WhaleTradesView } from "./features/desk/views/WhaleTradesView";
import "./index.css";

// Everything off the default route loads on demand; Shell shows a skeleton meanwhile.
const DashboardPage = lazy(() => import("./features/dashboard/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const UserDashboardPage = lazy(() => import("./features/dashboard/pages/UserDashboardPage").then((m) => ({ default: m.UserDashboardPage })));
const SegmentsPage = lazy(() => import("./features/segments/pages/SegmentsPage").then((m) => ({ default: m.SegmentsPage })));
const WalletsPage = lazy(() => import("./features/wallets/pages/WalletsPage").then((m) => ({ default: m.WalletsPage })));
const TerminalPage = lazy(() => import("./pages/TerminalPage").then((m) => ({ default: m.TerminalPage })));
const MainV2Page = lazy(() => import("./pages/PlaygroundLegacy").then((m) => ({ default: m.MainV2Page })));

/**
 * Router component - handles client-side routing
 */
function Router() {
	const [path, setPath] = useState(window.location.pathname);

	useEffect(() => {
		const handleLocationChange = () => {
			setPath(window.location.pathname);
		};
		window.addEventListener("popstate", handleLocationChange);
		return () => window.removeEventListener("popstate", handleLocationChange);
	}, []);

	if (path === "/legacy") {
		return (
			<Suspense fallback={null}>
				<MainV2Page />
			</Suspense>
		);
	}

	return (
		<ApiStatusProvider>
			<Shell>
				<Page path={path} />
			</Shell>
			<WalletDrawer />
			<CommandPalette />
		</ApiStatusProvider>
	);
}

const DESK_VIEWS: Record<string, () => preact.JSX.Element> = {
	"/smart-money": TopTradersView,
	"/smart-money/copy": CopyCandidatesView,
	"/flagged": FlaggedView,
	"/flagged/flow": FlaggedFlowView,
	"/whales": WhaleTradesView,
	"/whales/overview": WhalesOverviewView,
	"/watchlist": WatchlistView,
};

function Page({ path }: { path: string }) {
	const clean = path.length > 1 ? path.replace(/\/+$/, "") : path;
	const DeskView = DESK_VIEWS[clean];
	if (DeskView) {
		return <DeskView />;
	}

	// Spelled out, not ASK_ENABLED: the literal folds at build time, so AskView leaves the default bundle.
	if (process.env.BUN_PUBLIC_ASK_ENABLED === "1" && clean === "/ask") {
		return <AskView />;
	}

	if (path === "/wallets" || path.startsWith("/wallets/")) {
		return <WalletsPage />;
	}

	if (path === "/segments" || path.startsWith("/segments/")) {
		return <SegmentsPage />;
	}

	if (path.startsWith("/dashboard/users") || path.startsWith("/users")) {
		return <UserDashboardPage />;
	}

	if (path === "/dashboard" || path.startsWith("/dashboard/")) {
		return <DashboardPage />;
	}

	// The previous home page: alerts, detection stats and the copy backtest.
	if (path === "/terminal" || path.startsWith("/terminal/") || path === "/mainv2") {
		return <TerminalPage />;
	}

	return <UnusualMarketsView />;
}

/**
 * App component with providers
 *
 * Wraps the application with terminal providers and SWR config.
 */
export function App() {
	return (
		<AppProviders>
			<Router />
		</AppProviders>
	);
}

export default App;
